import { createGoogleGenerativeAI } from '@ai-sdk/google';
import { generateObject } from 'ai';
import { useConfigStore } from '@stores/v4/useConfigStore';
import { 
    NutritionLedgerSchema, 
    MetabolicAdviceSchema, 
    AIPrompts 
} from '@/shared/logic/ai-logic';

/**
 * AI NUTRITION SERVICE (v4)
 * Proxy layer for LLM interactions.
 * REFACTORED: Decoupled schemas and prompts into shared logic.
 */

const getProvider = () => {
    const apiKey = useConfigStore.getState().getActiveKey();
    if (!apiKey) console.error("[AI-Nutrition] CRITICAL: Active API Key is missing.");
    
    return createGoogleGenerativeAI({
        apiKey: apiKey || 'MISSING_KEY',
    });
};

export const AINutritionService = {
    async parseMealDescription(text: string) {
        try {
            const google = getProvider();
            const { object } = await generateObject({
                model: google('gemini-2.5-flash-lite'),
                schema: NutritionLedgerSchema,
                prompt: AIPrompts.nutritionParser(text),
            });

            if (object?.items) {
                const sanitizedItems = object.items.map((it: any) => {
                    // 1. Unify the core micros
                    const mergedMicros = { ...(it.micros || {}) };
                    
                    // 2. Map discovered micros into the same dictionary format to keep DB clean
                    if (it.discovered_micros && Array.isArray(it.discovered_micros)) {
                        it.discovered_micros.forEach((dm: any) => {
                            if (dm && dm.name && dm.amount > 0) {
                                // Normalize using the Canonical Registry
                                const rawName = dm.name.toLowerCase().replace(/[^a-z0-9\s]+/g, ' ').trim();
                                
                                // Direct snake_case conversion for the normalizer
                                const safeKey = rawName.replace(/\s+/g, '_');
                                
                                // Note: We do a local normalization here since we can't easily import from constants
                                // in this pure function without creating a circular dep or requiring a huge import.
                                // We'll rely on the existing mapping logic or just use safeKey if canonical is unknown.
                                // The backend / UI already uses normalizeNutrientKey during rendering, so saving safeKey is fine.
                                if (!mergedMicros[safeKey]) {
                                    mergedMicros[safeKey] = { amount: dm.amount, unit: dm.unit || 'mg' };
                                }
                            }
                        });
                    }

                    return {
                        ...it,
                        quantity: `${it.numeric_amount}${it.unit}`,
                        calories: Math.round(it.calories || 0),
                        protein: Number((it.protein || 0).toFixed(1)),
                        carbs: Number((it.carbs || 0).toFixed(1)),
                        fat: Number((it.fat || 0).toFixed(1)),
                        food_insulin_index: Math.round(it.food_insulin_index || 0),
                        insulin_load: Number((it.insulin_load || 0).toFixed(1)),
                        insulin_spike_risk: it.insulin_spike_risk || "Low",
                        micros: mergedMicros,
                        // Clean up so discovered_micros is NOT saved to the DB payload
                        discovered_micros: undefined
                    };
                });
                return { data: { ...object, items: sanitizedItems }, error: null };
            }
            return { data: object, error: null };
        } catch (err: any) {
            console.error("[AI-Nutrition] Parsing FAILED:", err);
            return { data: null, error: err.message };
        }
    },

    async generateMetabolicAdvice(stats: any, profile: any) {
        try {
            const google = getProvider();
            const { object } = await generateObject({
                model: google('gemini-2.5-flash-lite'),
                schema: MetabolicAdviceSchema,
                prompt: AIPrompts.metabolicCoach(stats, profile),
            });
            return { data: (object as any).advice, error: null };
        } catch (err: any) {
            console.error("[AI-Nutrition] Advice generation FAILED:", err);
            return { data: "Coach unavailable. Ensure your nutrition log is complete for analysis.", error: err.message };
        }
    }
};
